import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

export interface UseSearchableDropdownOptions<T> {
  items: T[];
  filterFn: (item: T, query: string) => boolean;
  getDisplayValue: (item: T) => string;
  onSelect: (item: T) => void;
  disabled?: boolean;
  initialValue?: string;
}

export interface UseSearchableDropdownReturn<T> {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showDropdown: boolean;
  setShowDropdown: (show: boolean) => void;
  filteredItems: T[];
  highlightedIndex: number;
  handleItemSelect: (item: T) => void;
  handleInputChange: (value: string) => void;
  handleInputFocus: () => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Custom hook for managing searchable dropdown functionality
 * Handles search query state, filtering, dropdown visibility, and click-outside detection
 */
export function useSearchableDropdown<T>({
  items,
  filterFn,
  getDisplayValue,
  onSelect,
  disabled = false,
  initialValue = '',
}: UseSearchableDropdownOptions<T>): UseSearchableDropdownReturn<T> {
  const [searchQuery, setSearchQuery] = useState(initialValue);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevInitialValueRef = useRef<string>(initialValue);

  // Sync initialValue changes (e.g., when items load and initialValue is computed)
  useEffect(() => {
    if (initialValue !== prevInitialValueRef.current) {
      setSearchQuery(initialValue);
      prevInitialValueRef.current = initialValue;
    }
  }, [initialValue]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    return items.filter(item => filterFn(item, searchQuery));
  }, [items, searchQuery, filterFn]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle item selection
  const handleItemSelect = useCallback((item: T) => {
    setSearchQuery(getDisplayValue(item));
    setShowDropdown(false);
    onSelect(item);
  }, [getDisplayValue, onSelect]);

  // Handle input change
  const handleInputChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (!disabled) {
      setShowDropdown(true);
    }
  }, [disabled]);

  // Handle input focus
  const handleInputFocus = useCallback(() => {
    if (!disabled) {
      setShowDropdown(true);
    }
  }, [disabled]);

  // Reset highlighted index when filtered items change (always select first option)
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredItems]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown || filteredItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredItems.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredItems.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
          handleItemSelect(filteredItems[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(0);
        break;
    }
  }, [showDropdown, filteredItems, highlightedIndex, handleItemSelect]);

  return {
    searchQuery,
    setSearchQuery,
    showDropdown,
    setShowDropdown,
    filteredItems,
    highlightedIndex,
    handleItemSelect,
    handleInputChange,
    handleInputFocus,
    handleKeyDown,
    containerRef,
  };
}
